import { inject } from '@angular/core'

import { BehaviorSubject, combineLatest, filter, Observable, Subject, takeUntil, timer } from 'rxjs'

import { AuthSession } from '@ti/core/auth/services'
import { ONE_MINUTE_MS } from '@ti/core/common/constants'
import { Memoize } from '@ti/core/common/decorators'
import { BrowserLocalStorage } from '@ti/core/common/storage'
import { injectDestroy$, injectIsPageInForeground$ } from '@ti/core/common/utils'

import { BaseFlespiClient } from './base-flespi.client'
import { BaseDeviceTelemetry, FLESPI_TOKEN_STORAGE_KEY, FlespiSubscriptionRef } from './contracts'

type TelemetryMap<T> = Map<number, T>

const MIN_AUTH_FAILURE_BACKOFF_MS = 2000
const MAX_AUTH_FAILURE_BACKOFF_MS = ONE_MINUTE_MS

export interface TelemetryProvider<T extends BaseDeviceTelemetry> {
  telemetry$: Observable<TelemetryMap<T>>
}

/**
 * Owns the flespi telemetry orchestration that is identical no matter what a context
 * tracks (fleet vehicles, raw tsp devices, ...): the telemetry store, resubscribing
 * whenever the flespi token rotates, and the base merge of partial updates into the
 * store. A subclass only needs to say which client it talks to and, if its telemetry
 * carries derived fields, how to compute them via `enrichTelemetryUpdate`.
 */
export abstract class BaseTelemetryProvider<
  T extends BaseDeviceTelemetry,
> implements TelemetryProvider<T> {
  protected readonly authSession = inject(AuthSession)
  protected readonly localStorage = inject(BrowserLocalStorage)
  protected readonly destroy$ = injectDestroy$()
  protected readonly isPageInForeground$ = injectIsPageInForeground$()

  protected abstract readonly flespiClient: BaseFlespiClient

  protected readonly telemetryMap$ = new BehaviorSubject<TelemetryMap<T>>(new Map())

  @Memoize()
  public get telemetry$(): Observable<TelemetryMap<T>> {
    return this.telemetryMap$.asObservable()
  }

  protected resetFlespiTokenOnSignOut(): void {
    this.authSession.state.onSignedOut$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.localStorage.setItem(FLESPI_TOKEN_STORAGE_KEY, '')
    })
  }

  /**
   * Keeps one live flespi subscription running, recreating it whenever the token
   * rotates, and only while the page is in the foreground. `subscribe` receives the
   * current token and opens the context-specific subscription (which client, which
   * topics, filtered to which vehicles/devices).
   */
  protected startTelemetrySubscription(
    subscribe: (token: string, onAuthFailure: () => void) => FlespiSubscriptionRef,
  ): FlespiSubscriptionRef {
    let currentStream: FlespiSubscriptionRef | undefined
    let currentFlespiToken: string | undefined
    let currentStreamGeneration = 0
    let refreshPromise: Promise<void> | undefined
    let refreshPending = false
    let forceFreshTokenOnNextRefresh = false
    let authFailureBackoffMs = 0
    let authFailureTimeout: ReturnType<typeof setTimeout> | undefined
    let stopped = false
    const stop$ = new Subject<void>()

    const clearAuthFailureTimeout = () => {
      if (authFailureTimeout) {
        clearTimeout(authFailureTimeout)
        authFailureTimeout = undefined
      }
    }

    const destroyCurrentStream = () => {
      currentStreamGeneration++
      currentStream?.destroy()
      currentStream = undefined
      currentFlespiToken = undefined
    }

    const onAuthFailure = (streamGeneration: number) => {
      if (
        stopped ||
        authFailureTimeout ||
        !currentStream ||
        streamGeneration !== currentStreamGeneration
      ) {
        return
      }

      // This stream has been rejected and must never be considered healthy again.
      destroyCurrentStream()

      authFailureBackoffMs = Math.min(
        authFailureBackoffMs ? authFailureBackoffMs * 2 : MIN_AUTH_FAILURE_BACKOFF_MS,
        MAX_AUTH_FAILURE_BACKOFF_MS,
      )
      authFailureTimeout = setTimeout(() => {
        authFailureTimeout = undefined
        void refresh(true).catch((error) => console.error(error))
      }, authFailureBackoffMs)
    }

    const refresh = (forceFreshToken: boolean): Promise<void> => {
      if (stopped) {
        return Promise.resolve()
      }

      refreshPending = true
      forceFreshTokenOnNextRefresh ||= forceFreshToken

      if (refreshPromise) {
        return refreshPromise
      }

      refreshPromise = (async () => {
        while (refreshPending && !stopped) {
          refreshPending = false
          const shouldForceFreshToken = forceFreshTokenOnNextRefresh
          forceFreshTokenOnNextRefresh = false
          const token = await this.flespiClient.getToken(shouldForceFreshToken)

          if (stopped) {
            return
          }

          if (currentStream && currentFlespiToken === token && !shouldForceFreshToken) {
            if (!authFailureTimeout) {
              authFailureBackoffMs = 0
            }
            continue
          }

          clearAuthFailureTimeout()
          destroyCurrentStream()

          if (stopped) {
            return
          }

          const streamGeneration = ++currentStreamGeneration
          const nextStream = subscribe(token, () => onAuthFailure(streamGeneration))
          if (stopped) {
            nextStream.destroy()
            return
          }

          currentFlespiToken = token
          currentStream = nextStream
          if (!shouldForceFreshToken) {
            authFailureBackoffMs = 0
          }
        }
      })().finally(() => {
        refreshPromise = undefined
        if (refreshPending && !stopped) {
          void refresh(false).catch((error) => console.error(error))
        }
      })

      return refreshPromise
    }

    combineLatest([timer(16, 2 * ONE_MINUTE_MS), this.isPageInForeground$])
      .pipe(
        filter(([, isPageInForeground]) => isPageInForeground),
        takeUntil(stop$),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        void refresh(false).catch((error) => console.error(error))
      })

    const subscriptionRef: FlespiSubscriptionRef = {
      destroy: () => {
        if (stopped) {
          return
        }

        stopped = true
        refreshPending = false
        forceFreshTokenOnNextRefresh = false
        clearAuthFailureTimeout()
        stop$.next()
        stop$.complete()
        destroyCurrentStream()
      },
    }

    this.destroy$.pipe(takeUntil(stop$)).subscribe(() => subscriptionRef.destroy())

    return subscriptionRef
  }

  protected updateTelemetryMap(data: Partial<T> | Partial<T>[]): void {
    if (!Array.isArray(data)) {
      data = [data]
    }

    // Copy-on-write: consumers compare references, so nothing already emitted is mutated
    const telemetryMap = new Map(this.telemetryMap$.getValue())
    data.forEach((updates) => {
      if (!updates.deviceId) {
        console.warn(`No device id in telemetry data`, updates)
        return
      }

      // Ensure timestamps value set
      updates.timestamps = updates.timestamps || {}

      const current = telemetryMap.get(updates.deviceId)

      this.enrichTelemetryUpdate(updates, current)

      if (current) {
        telemetryMap.set(updates.deviceId, {
          ...current,
          ...updates,
          timestamps: { ...(current.timestamps || {}), ...(updates.timestamps || {}) },
        } as T)
      } else {
        telemetryMap.set(updates.deviceId, updates as T)
      }
    })

    this.telemetryMap$.next(telemetryMap)
  }

  /**
   * Hook for context-specific enrichment (derived status, position history, sensor
   * merges, ...) applied to `updates` in place before it's merged into the store.
   * No-op by default - override where the flespi context needs it.
   */
  protected enrichTelemetryUpdate(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    updates: Partial<T>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    current: T | undefined,
  ): void {}
}
